import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Tooltip,
  useTheme,
  alpha,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Avatar
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  AccessTime as TimeIcon,
  History as HistoryIcon
} from '@mui/icons-material';

// Import services
import * as timeTrackingService from '../../../services/timeTrackingService';
import useNotificationStore from '../../../stores/notificationStore';
import useAuthStore from '../../../stores/authStore';
import useTaskStore from '../../../stores/taskStore';

const TaskTimeTracker = ({ task, onTimeUpdate }) => {
  const theme = useTheme();
  const { showSuccess, showError } = useNotificationStore();
  const { user } = useAuthStore();
  const { updateTask } = useTaskStore();

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyTab, setHistoryTab] = useState(0); // 0 = summary, 1 = details
  const [currentTime, setCurrentTime] = useState(Date.now());
  
  // Debug logging
  // console.log('[TaskTimeTracker] Render:', {
  //   taskId: task?._id,
  //   taskTitle: task?.title,
  //   timeEntriesCount: task?.timeEntries?.length || 0,
  //   timeEntries: task?.timeEntries,
  //   userId: user?._id
  // });

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Get time entries from task
  const timeEntries = task?.timeEntries || [];

  // Find active timer for current user
  const myActiveTimer = useMemo(() => {
    console.log('[TaskTimeTracker] Looking for active timer:', {
      currentUserId: user?._id,
      timeEntries: timeEntries.map(entry => ({
        entryId: entry._id,
        userId: entry.user?._id || entry.user,
        userIdType: typeof (entry.user?._id || entry.user),
        isActive: entry.isActive,
        startTime: entry.startTime,
        endTime: entry.endTime,
        userFullObject: entry.user
      }))
    });
    
    const activeTimer = timeEntries.find(entry => {
      const entryUserId = entry.user?._id || entry.user;
      const currentUserId = user?._id;
      const userMatches = entryUserId === currentUserId;
      
      console.log('[TaskTimeTracker] Checking entry:', {
        entryUserId,
        currentUserId,
        userMatches,
        isActive: entry.isActive,
        result: entry.isActive && userMatches
      });
      
      return entry.isActive && userMatches;
    });
    
    console.log('[TaskTimeTracker] Active timer result:', {
      foundActiveTimer: !!activeTimer,
      activeTimerDetails: activeTimer
    });
    
    return activeTimer;
  }, [timeEntries, user]);

  // Calculate totals by user
  const userTotals = useMemo(() => {
    const totals = {};
    
    timeEntries.forEach(entry => {
      const userId = entry.user?._id || entry.user;
      const userName = entry.user?.firstName ? 
        `${entry.user.firstName} ${entry.user.lastName}` : 
        entry.user?.name || entry.user?.email || 'Unknown User';
      
      if (!totals[userId]) {
        totals[userId] = {
          userId,
          userName,
          totalDuration: 0,
          entryCount: 0,
          activeTimer: null
        };
      }
      
      if (entry.isActive) {
        const elapsed = currentTime - new Date(entry.startTime).getTime();
        totals[userId].totalDuration += elapsed;
        totals[userId].activeTimer = entry;
      } else {
        totals[userId].totalDuration += entry.duration || 0;
      }
      totals[userId].entryCount += 1;
    });
    
    return Object.values(totals);
  }, [timeEntries, currentTime]);

  // My total time
  const myTotalTime = userTotals.find(t => t.userId === user?._id)?.totalDuration || 0;

  // Overall total time
  const totalTaskTime = userTotals.reduce((sum, user) => sum + user.totalDuration, 0);

  const formatDuration = (ms) => {
    if (!ms || ms <= 0) return '0m';
    
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString();
  };

  const handleStartTimer = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const response = await timeTrackingService.startTimer(task._id);
      showSuccess('Timer started');
      
      // Reload task to get updated time entries
      if (onTimeUpdate) {
        await onTimeUpdate();
      }
    } catch (error) {
      showError(error.response?.data?.message || error.message || 'Failed to start timer');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopTimer = async () => {
    if (isLoading || !myActiveTimer) return;
    
    setIsLoading(true);
    try {
      await timeTrackingService.stopTimer(task._id);
      showSuccess('Timer stopped');
      
      // Reload task to get updated time entries
      if (onTimeUpdate) {
        await onTimeUpdate();
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to stop timer');
    } finally {
      setIsLoading(false);
    }
  };

  const getElapsedTime = () => {
    if (!myActiveTimer) return 0;
    return currentTime - new Date(myActiveTimer.startTime).getTime();
  };

  return (
    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimeIcon color="secondary" />
          <Typography variant="h6">Time Tracking</Typography>
        </Box>
      </Box>

      {/* Timer Display */}
      {myActiveTimer ? (
        <Box
          sx={{
            p: 2,
            mb: 2,
            border: 2,
            borderColor: 'success.main',
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.success.main, 0.1)
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2" color="success.main" fontWeight="bold">
                Timer Active
              </Typography>
              <Typography variant="h4" color="success.main">
                {formatDuration(getElapsedTime())}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Started: {formatDateTime(myActiveTimer.startTime)}
              </Typography>
            </Box>
            
            <Button
              variant="contained"
              color="error"
              startIcon={<StopIcon />}
              onClick={handleStopTimer}
              disabled={isLoading}
            >
              Stop Timer
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={isLoading ? <CircularProgress size={20} /> : <PlayIcon />}
            onClick={handleStartTimer}
            disabled={isLoading}
            sx={{ mb: 1 }}
          >
            Start Timer
          </Button>
          <Typography variant="caption" color="textSecondary" display="block">
            Click to start tracking time for this task
          </Typography>
        </Box>
      )}

      {/* Time Summary */}
      {timeEntries.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Time Summary
          </Typography>
          
          {/* User's time */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Your total time:</Typography>
            <Chip 
              label={formatDuration(myTotalTime)} 
              size="small" 
              color="secondary"
              variant="outlined"
            />
          </Box>
          
          {/* Total task time */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Total task time:</Typography>
            <Chip 
              label={formatDuration(totalTaskTime)} 
              size="small" 
              color="secondary"
              variant="outlined"
            />
          </Box>

          {/* Contributors count */}
          {userTotals.length > 1 && (
            <Typography variant="caption" color="textSecondary">
              {userTotals.length} contributors
            </Typography>
          )}
        </Box>
      )}

      {/* View History Button */}
      {timeEntries.length > 0 && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setShowHistoryDialog(true)}
          >
            View History ({timeEntries.length} entries)
          </Button>
        </Box>
      )}

      {/* History Dialog */}
      <Dialog 
        open={showHistoryDialog} 
        onClose={() => setShowHistoryDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Tabs value={historyTab} onChange={(e, v) => setHistoryTab(v)}>
            <Tab label="Summary" />
            <Tab label="All Entries" />
          </Tabs>
        </DialogTitle>
        <DialogContent>
          {historyTab === 0 ? (
            // Summary View
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell align="right">Total Time</TableCell>
                    <TableCell align="right">Entries</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userTotals.map((userTotal) => (
                    <TableRow key={userTotal.userId}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24 }}>
                            {userTotal.userName.charAt(0)}
                          </Avatar>
                          {userTotal.userName}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatDuration(userTotal.totalDuration)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{userTotal.entryCount}</TableCell>
                      <TableCell align="center">
                        {userTotal.activeTimer ? (
                          <Chip label="Active" size="small" color="success" />
                        ) : (
                          <Chip label="Inactive" size="small" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            // Detailed Entries View
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell>Start Time</TableCell>
                    <TableCell>End Time</TableCell>
                    <TableCell align="right">Duration</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeEntries.map((entry, index) => {
                    const duration = entry.isActive ? 
                      (currentTime - new Date(entry.startTime).getTime()) : 
                      entry.duration;
                    
                    return (
                      <TableRow key={entry._id || index}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24 }}>
                              {entry.user?.firstName?.charAt(0) || '?'}
                            </Avatar>
                            {entry.user?.firstName} {entry.user?.lastName}
                          </Box>
                        </TableCell>
                        <TableCell>{formatDateTime(entry.startTime)}</TableCell>
                        <TableCell>
                          {entry.endTime ? formatDateTime(entry.endTime) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={entry.isActive ? 'bold' : 'normal'}>
                            {formatDuration(duration)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {entry.isActive ? (
                            <Chip label="Active" size="small" color="success" />
                          ) : (
                            <Chip label="Completed" size="small" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistoryDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskTimeTracker;